import * as React from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { getJSON, postJSON } from '@/lib/api'
import type { Invoice, PaymentForm } from '@/pages/InvoicesPage.types'

type PaymentAllocationSuggestionResponse = {
  allocations: Array<{ invoice_id: number; amount: string; amount_cents: number }>
}

export interface PaymentAllocationQuote {
  payment_cents: number
  payment: string
  allocated_cents: number
  allocated: string
  account_credit_cents: number
  account_credit: string
  currency: string
}

export function formAllocations(selected: Record<number, string>) {
  return Object.entries(selected)
    .filter(([, amount]) => amount.trim() !== '')
    .map(([invoiceId, amount]) => ({ invoiceId: Number(invoiceId), amount }))
}

export function useAllocationSuggestion(
  invoice: Invoice,
  form: PaymentForm,
  setSelected: React.Dispatch<React.SetStateAction<Record<number, string>>>,
  onChange: (form: PaymentForm) => void,
) {
  const formRef = React.useRef(form)
  formRef.current = form
  return useMutation<
    PaymentAllocationSuggestionResponse,
    Error,
    {
      amount: string
      invoiceId?: number
      requestId?: number
      latestRequest?: React.RefObject<number>
    }
  >({
    mutationFn: ({ amount, invoiceId }) =>
      getJSON<PaymentAllocationSuggestionResponse>(
        `/api/v1/contacts/${invoice.contact_id}/payment_allocation_suggestion?${new URLSearchParams(
          {
            amount,
            ...(invoiceId ? { invoice_id: String(invoiceId) } : {}),
          },
        )}`,
      ),
    onSuccess: (data, request) => {
      if (request.requestId !== undefined && request.latestRequest?.current !== request.requestId)
        return
      const next = Object.fromEntries(
        data.allocations.map((allocation) => [
          allocation.invoice_id,
          allocation.amount.replace(/[^0-9.-]/g, ''),
        ]),
      )
      setSelected(next)
      onChange({ ...formRef.current, allocations: formAllocations(next) })
    },
  })
}

export function usePaymentAllocationQuote(
  contactId: number,
  amount: string,
  selected: Record<number, string>,
) {
  const request = React.useMemo(
    () => ({
      amount,
      allocations: formAllocations(selected).map((item) => ({
        invoice_id: item.invoiceId,
        amount: item.amount,
      })),
    }),
    [amount, selected],
  )

  return useQuery({
    queryKey: ['payment-allocation-quote', contactId, request],
    queryFn: () =>
      postJSON<{ quote: PaymentAllocationQuote }>(
        `/api/v1/contacts/${contactId}/payment_allocation_quote`,
        { quote: request },
      ),
    enabled: request.amount.trim() !== '',
    retry: false,
  })
}
